import { lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { ConvexProviderWrapper } from "./providers/ConvexProvider";
import { UserProvider } from "./contexts/UserContext";
import { Loader2 } from "lucide-react";

// Lazy load route components
const Index = lazy(() => import("./pages/Index"));
const NotFound = lazy(() => import("./pages/NotFound"));
const Admin = lazy(() => import("./pages/Admin").then(module => ({ default: module.Admin })));

// Patient routes (new architecture)
const PatientLayout = lazy(() => import("./layouts/PatientLayout").then(module => ({ default: module.PatientLayout })));
const SmartPatientRedirect = lazy(() => import("./pages/patient/SmartPatientRedirect"));
const PatientOverview = lazy(() => import("./pages/patient/PatientOverview"));
const PatientDocuments = lazy(() => import("./pages/patient/PatientDocuments"));
const PatientAnalysis = lazy(() => import("./pages/patient/PatientAnalysis"));
const PatientResearch = lazy(() => import("./pages/patient/PatientResearch"));
const PatientReport = lazy(() => import("./pages/patient/PatientReport"));

// Design preview (no auth required)
const DesignPreview = lazy(() => import("./pages/DesignPreview"));
const DesignSystem = lazy(() => import("./pages/DesignSystem"));
const DesignSystemMinimal = lazy(() => import("./pages/DesignSystemMinimal"));


const queryClient = new QueryClient();

// Loading component for lazy loaded routes
const PageLoader = () => (
  <div className="flex items-center justify-center min-h-screen">
    <Loader2 className="h-8 w-8 animate-spin text-primary" />
  </div>
);

const App = () => (
  <ConvexProviderWrapper>
    <UserProvider>
      <QueryClientProvider client={queryClient}>
        <TooltipProvider>
          <Toaster />
          <Sonner />
          <BrowserRouter>
            <Suspense fallback={<PageLoader />}>
              <Routes>
                <Route path="/" element={<Index />} />
                <Route path="/admin" element={<Admin />} />
                <Route path="/design-preview" element={<DesignPreview />} />
                <Route path="/design-system" element={<DesignSystem />} />
                <Route path="/design-system-minimal" element={<DesignSystemMinimal />} />
                
                {/* New route-based patient architecture */}
                <Route path="/patient/:patientId" element={<PatientLayout />}>
                  <Route index element={<SmartPatientRedirect />} />
                  <Route path="overview" element={<PatientOverview />} />
                  <Route path="documents" element={<PatientDocuments />} />
                  <Route path="data" element={<PatientDocuments />} /> {/* Redirect old /data to /documents */}
                  <Route path="analysis" element={<PatientAnalysis />} />
                  <Route path="research" element={<PatientResearch />} />
                  <Route path="report" element={<PatientReport />} />
                </Route>
                
                <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
          </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
  </UserProvider>
  </ConvexProviderWrapper>
);

export default App;